import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { revalidateLogic, useStore } from '@tanstack/react-form';

import { mutateAudienceCreate } from '@/api/audience';
import { useIntegrationCRMProviders } from '@/api/integration';
import AudienceBaseForm from '@/app/audiences/AudienceBaseForm';
import PlugConnect from '@/assets/svg/bx-plug-connect.svg?react';
import { Button, LinkButton } from '@/components/buttons';
import { useAppForm } from '@/components/Form';
import { PageSpinner } from '@/components/loaders';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import { PendoPlaceholder } from '@/components/PendoPlaceholder';
import { Results } from '@/components/Results';
import { ReturnToLink } from '@/components/ReturnToLink';
import { SupportLink } from '@/components/typography';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import SolAPIError from '@/error/SolAPIError';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import ErrorOrChildren from '@/features/ErrorOrChildren';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import ImportCRMAudienceHubspotForm from './ImportCRMAudienceHubspotForm';
import ImportCRMAudienceSalesforceForm from './ImportCRMAudienceSalesforceForm';

const ImportCRMAudience = () => {
  const navigate = useInstanceNavigate();
  const { pushNotification } = useNotification();
  const { providers, hasHubspotConnection, hasSalesforceConnection, isLoading, error } =
    useIntegrationCRMProviders();

  const hasNoCRMConnection = !hasHubspotConnection && !hasSalesforceConnection;
  const isSalesforceImport = hasSalesforceConnection;

  const form = useAppForm({
    defaultValues: {
      hubspot_url: '',
      salesforce_url: '',
      name: '',
      description: '',
    },
    validationLogic: revalidateLogic(),
    validators: {
      onSubmitAsync: async ({ value }) => {
        try {
          const { hubspot_url, salesforce_url, ...rest } = value;

          await mutateAudienceCreate({
            type: hubspot_url ? 'hubspot' : 'salesforce',
            hubspot_url: hubspot_url || undefined,
            salesforce_url: salesforce_url || undefined,
            ...rest,
          });

          navigate(ROUTES.audiences.path, {
            state: {
              successMessage: t`Audience was successfully created.`,
            },
          });
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          const pageErrors =
            error instanceof SolAPIError
              ? Object.values(error.errors).filter(
                  (solError) => !solError.extensions.details?.fieldName,
                )
              : [];

          pageErrors.forEach((solError) => {
            pushNotification({ type: 'error', message: solError.message });
          });

          if (fieldErrors || pageErrors.length) {
            return fieldErrors;
          }

          pushNotification({
            type: 'error',
            message: t`An unknown error has occurred, please try again or contact support.`,
          });
        }
      },
    },
  });

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  const panelInfoPopover = isSalesforceImport
    ? {
        title: <Trans>Import Salesforce Report</Trans>,
        body: (
          <Trans>
            Salesforce Audience creation uses a standard "Accounts" report to create an audience
            usable for measurement within Channel99. Only "Accounts" reports can be used to create
            audiences.{' '}
            <SupportLink variant="body2" color="static-green" path="CREATE_SALESFORCE_AUDIENCE">
              See instructions here.
            </SupportLink>
          </Trans>
        ),
      }
    : {
        title: <Trans>Import HubSpot Report</Trans>,
        body: (
          <Trans>
            HubSpot Audience creation uses a standard "Companies" Segment List in HubSpot to create
            an audience usable for measurement within Channel99. Only "Companies" lists with a
            "Company Domain Name" column can be used to create audiences.{' '}
            <SupportLink variant="body2" color="static-green" path="CREATE_HUBSPOT_AUDIENCE">
              See instructions here.
            </SupportLink>
          </Trans>
        ),
      };

  return (
    <Page title={t`New Audience`} exampleAIQuestions={exampleAIQuestions('audience')}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppForm>
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <ReturnToLink route={ROUTES.audiences.path} routeName={t`Audiences`} />
              <div className="flex items-center gap-4">
                <PendoPlaceholder />
                <LinkButton to={ROUTES.audiences.path}>
                  <Trans>Cancel</Trans>
                </LinkButton>
                {hasNoCRMConnection ? (
                  <Button variant="primary" disabled>
                    <Trans>Save</Trans>
                  </Button>
                ) : (
                  <form.SubmitButton variant="primary">
                    <Trans>Save</Trans>
                  </form.SubmitButton>
                )}
              </div>
            </div>

            <ErrorOrChildren
              error={error}
              message404={<Trans>The CRM you're looking for does not exist</Trans>}
            >
              {!providers && isLoading && <PageSpinner />}

              {!isLoading && hasNoCRMConnection && (
                <Results
                  icon={<PlugConnect />}
                  title={<Trans>Connect your CRM</Trans>}
                  subtitle={<Trans>Please connect your CRM to import audiences.</Trans>}
                  extra={
                    <LinkButton variant="primary" to={ROUTES.integrationByCategory('crm')}>
                      <Trans>Connect CRM</Trans>
                    </LinkButton>
                  }
                  isFullPage
                />
              )}

              {!hasNoCRMConnection && (
                <Panel title={<Trans>Import CRM Report</Trans>} infoPopover={panelInfoPopover}>
                  {isSalesforceImport ? (
                    <ImportCRMAudienceSalesforceForm
                      form={form}
                      fields={{
                        salesforce_url: 'salesforce_url',
                      }}
                      isCreatingAudience={isSubmitting}
                    />
                  ) : (
                    <ImportCRMAudienceHubspotForm
                      form={form}
                      fields={{
                        hubspot_url: 'hubspot_url',
                      }}
                      isCreatingAudience={isSubmitting}
                    />
                  )}
                  <AudienceBaseForm
                    form={form}
                    fields={{
                      name: 'name',
                      description: 'description',
                    }}
                    audience={undefined}
                  />
                </Panel>
              )}
            </ErrorOrChildren>
          </div>
        </form.AppForm>
      </form>
    </Page>
  );
};

export default ImportCRMAudience;
